import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ResponseOf } from "../../../../core/api";
import useApi from "../../../../shared/hooks/useApi";
import type { PatientProfilePatient } from "../../patientProfileModal.types";

export type PatientRecentActivityChange = {
  category?: string | null;
  fieldType?: string | null;
  new?: unknown;
  old?: unknown;
  title: string;
};

export type PatientRecentActivityRow = {
  actionStr: string;
  changes: PatientRecentActivityChange[];
  createdAt?: unknown;
  customName?: string | null;
  id: number | string;
  subTable?: string | null;
  subTableDisplay?: string | null;
  subTableId?: number | string | null;
  tableName?: string | null;
  updated?: unknown;
  updatedAt?: unknown;
  updatedBy?: number | string | null;
  updatedByUser?: string | null;
  updatedByUserType?: string | null;
};

export type PatientRecentActivityResponse = {
  list: PatientRecentActivityRow[];
};

export type PatientRecentActivityAgencyMemberUserIdsResponse = {
  list: Array<{
    agencyMemberId: number | string;
    userId: number | string;
  }>;
};

type AgencyMember = ResponseOf<
  "get",
  "/agencies/:agencyId/agency_members"
>["agencyMembers"][number];
type PatientAuditResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/audit/table/patient/id/:patientId"
>;

function normalizePatientRecentActivityChange(value: unknown): PatientRecentActivityChange | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const title = Reflect.get(value, "title");

  if (typeof title !== "string") {
    return null;
  }

  const category = Reflect.get(value, "category");
  const fieldType = Reflect.get(value, "fieldType");

  return {
    category: typeof category === "string" ? category : null,
    fieldType: typeof fieldType === "string" ? fieldType : null,
    new: Reflect.get(value, "new"),
    old: Reflect.get(value, "old"),
    title,
  };
}

function normalizePatientRecentActivity(
  response: PatientAuditResponse
): PatientRecentActivityResponse {
  return {
    list: response.list.map((row) => ({
      ...row,
      changes: Array.isArray(row.changes)
        ? row.changes.flatMap((change) => {
            const normalizedChange = normalizePatientRecentActivityChange(change);

            return normalizedChange === null ? [] : [normalizedChange];
          })
        : [],
      createdAt: row.updated,
      updatedAt: row.updated,
    })),
  };
}

export function usePatientRecentActivity(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["patientProfileModal", patientId, "recentActivity"],
    queryFn: () =>
      api
        .get("/agencies/:agencyId/audit/table/patient/id/:patientId", {
          path: { patientId },
        })
        .then(normalizePatientRecentActivity),
  });
}

export function usePatientRecentActivityActorPhotos() {
  const { api } = useApi();

  const agencyMembersQuery = useQuery({
    queryKey: ["patientProfileModal", "recentActivity", "agencyMembers"],
    queryFn: () => api.get("/agencies/:agencyId/agency_members", {}),
    staleTime: Infinity,
  });

  const agencyMemberUserIdsQuery = useQuery({
    queryKey: ["patientProfileModal", "recentActivity", "agencyMemberUserIds"],
    queryFn: () => api.get("/agencies/:agencyId/agency_members_user_ids", {}),
    staleTime: Infinity,
  });

  const agencyMembersByUserId = useMemo(
    () =>
      createAgencyMembersByUserId(
        agencyMembersQuery.data?.agencyMembers ?? [],
        agencyMemberUserIdsQuery.data?.list ?? []
      ),
    [agencyMemberUserIdsQuery.data?.list, agencyMembersQuery.data?.agencyMembers]
  );

  return {
    agencyMembers: agencyMembersQuery.data?.agencyMembers ?? [],
    agencyMembersByUserId,
    isError: agencyMembersQuery.isError || agencyMemberUserIdsQuery.isError,
    isLoading: agencyMembersQuery.isLoading || agencyMemberUserIdsQuery.isLoading,
  };
}

function createAgencyMembersByUserId(
  agencyMembers: AgencyMember[],
  agencyMemberUserIds: PatientRecentActivityAgencyMemberUserIdsResponse["list"]
) {
  const agencyMembersById = new Map(
    agencyMembers.map((agencyMember) => [String(agencyMember.id), agencyMember])
  );
  const agencyMembersByUserId = new Map<string, AgencyMember>();

  for (const row of agencyMemberUserIds) {
    const agencyMember = agencyMembersById.get(String(row.agencyMemberId));

    if (agencyMember !== undefined) {
      agencyMembersByUserId.set(String(row.userId), agencyMember);
    }
  }

  return agencyMembersByUserId;
}
